import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api, ApiError, type DemoPerson } from '@/api/client';
import { DemoLayout } from '@/components/DemoLayout';
import { EnrollStepper } from '@/components/EnrollStepper';
import { COUNTRIES, DEFAULT_NATIONALITY } from '@/lib/countries';
import { applyCpfMask, isValidCpf, stripDigits } from '@/lib/cpf';
import { readEnrollment } from '@/lib/enrollment';

type Form = {
  fullName: string;
  email: string;
  birthDate: string;
  nationality: string;
  document: string;
};

type Errors = Partial<Record<keyof Form, string>>;

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

export function validateForm(form: Form, today = new Date()): Errors {
  const errors: Errors = {};
  if (form.fullName.trim().replace(/\s+/g, ' ').length < 2) errors.fullName = 'Informe o nome completo.';
  if (!EMAIL.test(form.email.trim())) errors.email = 'Informe um e-mail válido.';
  const match = DATE.exec(form.birthDate);
  if (!match) {
    errors.birthDate = 'Informe a data de nascimento.';
  } else {
    const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    if (
      date.getFullYear() !== Number(match[1]) ||
      date.getMonth() !== Number(match[2]) - 1 ||
      date.getDate() !== Number(match[3])
    ) {
      errors.birthDate = 'Data inválida.';
    } else if (date > today) {
      errors.birthDate = 'A data não pode estar no futuro.';
    }
  }
  if (!form.nationality) errors.nationality = 'Escolha a nacionalidade.';
  if (form.nationality === 'BR') {
    if (!isValidCpf(form.document)) errors.document = 'CPF inválido.';
  } else if (form.document.trim().length < 6 || !/^[A-Za-z0-9\- ]+$/.test(form.document.trim())) {
    errors.document = 'Informe o número do documento (mínimo 6 caracteres).';
  }
  return errors;
}

export type SuccessState = { person: DemoPerson; indexJobId: string | null; confidence: number | null };

/** Captura de dados — os mesmos campos do /signup do onboarding. */
export function DataPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const enrollment = readEnrollment();
  const confidence =
    location.state !== null && typeof location.state === 'object' && 'confidence' in location.state
      ? Number((location.state as { confidence: unknown }).confidence)
      : null;

  const [form, setForm] = useState<Form>({
    fullName: '',
    email: '',
    birthDate: '',
    nationality: DEFAULT_NATIONALITY,
    document: '',
  });
  const [touched, setTouched] = useState<Partial<Record<keyof Form, boolean>>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!enrollment) navigate('/cadastrar', { replace: true });
    else if (!enrollment.selfieDone) navigate('/cadastrar/selfie', { replace: true });
  }, [enrollment, navigate]);

  if (!enrollment) return null;

  const errors = validateForm(form);
  const isValid = Object.keys(errors).length === 0;
  const show = (field: keyof Form) => (touched[field] ? errors[field] : undefined);

  const set = (field: keyof Form) => (value: string) => {
    setForm((current) => {
      if (field === 'nationality') {
        return { ...current, nationality: value, document: '' };
      }
      if (field === 'document' && current.nationality === 'BR') {
        return { ...current, document: applyCpfMask(value) };
      }
      return { ...current, [field]: value };
    });
  };

  const blur = (field: keyof Form) => () => setTouched((t) => ({ ...t, [field]: true }));

  const handleSubmit = async () => {
    setTouched({ fullName: true, email: true, birthDate: true, nationality: true, document: true });
    if (!isValid) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const { person } = await api.enroll.complete({
        enrollmentId: enrollment.enrollmentId,
        fullName: form.fullName.trim().replace(/\s+/g, ' '),
        email: form.email.trim().toLowerCase(),
        birthDate: form.birthDate,
        nationality: form.nationality,
        document: form.nationality === 'BR' ? stripDigits(form.document) : form.document.trim(),
      });
      const state: SuccessState = { person, indexJobId: enrollment.indexJobId, confidence };
      navigate('/cadastrar/sucesso', { replace: true, state });
    } catch (err) {
      if (err instanceof ApiError && err.code === 'SELFIE_REQUIRED_FIRST') {
        navigate('/cadastrar/selfie', { replace: true });
        return;
      }
      setSubmitError(err instanceof ApiError ? err.message : 'Não foi possível concluir o cadastro.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DemoLayout title="Cadastro">
      <EnrollStepper current="data" />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography variant="h2">Seus dados</Typography>
        <Typography variant="body2" color="text.secondary">
          Biometria pronta{confidence !== null ? ` (confiança ${Math.round(confidence)}%)` : ''}. Agora
          preencha quem é você.
        </Typography>
      </Box>

      <Box
        component="form"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void handleSubmit();
        }}
      >
        <TextField
          id="fullName"
          label="Nome completo"
          value={form.fullName}
          onChange={(e) => set('fullName')(e.target.value)}
          onBlur={blur('fullName')}
          error={Boolean(show('fullName'))}
          helperText={show('fullName')}
          autoComplete="name"
        />
        <TextField
          id="email"
          label="E-mail"
          type="email"
          value={form.email}
          onChange={(e) => set('email')(e.target.value)}
          onBlur={blur('email')}
          error={Boolean(show('email'))}
          helperText={show('email')}
          inputMode="email"
          autoComplete="email"
        />
        <TextField
          id="birthDate"
          label="Data de nascimento"
          type="date"
          value={form.birthDate}
          onChange={(e) => set('birthDate')(e.target.value)}
          onBlur={blur('birthDate')}
          error={Boolean(show('birthDate'))}
          helperText={show('birthDate')}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          id="nationality"
          select
          label="Nacionalidade"
          value={form.nationality}
          onChange={(e) => set('nationality')(e.target.value)}
          onBlur={blur('nationality')}
          error={Boolean(show('nationality'))}
          helperText={show('nationality')}
        >
          {COUNTRIES.map((c) => (
            <MenuItem key={c.code} value={c.code}>
              {c.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          id="document"
          label={form.nationality === 'BR' ? 'CPF' : 'Documento (passaporte ou ID)'}
          value={form.document}
          onChange={(e) => set('document')(e.target.value)}
          onBlur={blur('document')}
          error={Boolean(show('document'))}
          helperText={show('document') ?? 'Exibido sempre com máscara.'}
          inputMode={form.nationality === 'BR' ? 'numeric' : 'text'}
          placeholder={form.nationality === 'BR' ? '000.000.000-00' : ''}
        />

        {submitError && (
          <Typography color="error" role="alert" sx={{ mt: 1 }}>
            {submitError}
          </Typography>
        )}

        <Box sx={{ mt: 3, pb: 2 }}>
          <Button type="submit" variant="contained" fullWidth disabled={submitting}>
            {submitting ? 'Salvando…' : 'Concluir cadastro'}
          </Button>
        </Box>
      </Box>
    </DemoLayout>
  );
}
